import React from "react";
import { Button } from "antd";
import { FormattedMessage } from "react-intl";
import crown from "../../assets/images/crown.svg";
import tick from "../../assets/images/tick.svg";
// The card owns its styles. Until now they arrived only because Subscription.js
// imported the sheet and CRA emits a single CSS bundle — the home card would have
// rendered unstyled the day anyone code-split a route.
import "../../views/Subscription/style.css";

// The bullets both cards shipped before plans were data. A plan saved with no
// feature keys falls back to these, so pasting a price id and nothing else still
// renders a complete card.
const DEFAULT_FEATURE_KEYS = [
  "view_profile",
  "like",
  "share",
  "contract",
  "chat",
];

const CURRENCY_SYMBOLS = { usd: "$" };

/**
 * "$99.99" — the amount only. The interval is a separate translated node so the
 * Spanish page does not render an English "/ month". Returns null when the
 * amount is absent so the caller can render a placeholder instead of
 * "$undefined", which is what the home card used to show while loading.
 */
export const formatAmount = (plan) => {
  const amount = Number(plan?.amount);
  if (plan?.amount === null || plan?.amount === undefined) {
    return null;
  }
  if (!Number.isFinite(amount)) {
    return null;
  }
  const currency = String(plan?.currency || "usd").toLowerCase();
  const symbol = CURRENCY_SYMBOLS[currency] || "";
  const suffix = symbol ? "" : ` ${currency.toUpperCase()}`;
  return `${symbol}${amount}${suffix}`;
};

/**
 * The plain "/ month" or "/ year" suffix, used only when the price really is
 * billed every single interval. `verifyPrice` refuses anything else now, but a
 * row created before that guard could still carry `intervalCount > 1`, and a
 * card must never advertise a period Stripe will not bill on.
 */
export const intervalMessageId = (plan) =>
  plan?.interval === "year" ? "per_year" : "per_month";

/**
 * The word the narrow-screen toggle puts on a plan's button. Falls back to the
 * plan's own name when Stripe bills on something other than a plain month or
 * year, so a "every 3 months" plan is never labelled "Monthly".
 */
export const toggleMessageId = (plan) =>
  plan?.interval === "year" ? "plan_yearly" : "plan_monthly";

export const hasSimpleInterval = (plan) =>
  Number(plan?.intervalCount ?? 1) === 1 &&
  (plan?.interval === "year" || plan?.interval === "month");

export const PlanCard = ({
  plan,
  ctaLabel,
  onCta,
  ctaLoading,
  ctaDisabled,
  selected,
  onSelect,
  showTrial = true,
}) => {
  const amount = formatAmount(plan);
  const featureKeys = plan?.featureKeys?.length
    ? plan.featureKeys
    : DEFAULT_FEATURE_KEYS;
  // No Bootstrap display utilities here. `.subscription-card` already declares
  // display:flex, flex-direction:column and align-items:center, and Bootstrap
  // emits its utilities as `!important` — so `d-flex` silently outranked the
  // stylesheet and made the card impossible to hide on mobile.
  const cardClass = [
    "subscription-card",
    plan?.highlighted ? "subscription-card--highlighted" : "",
    selected ? "subscription-card--selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cardClass}
      onClick={onSelect}
      role={onSelect ? "button" : undefined}
      data-testid={plan?.id ? `plan-card-${plan.id}` : "plan-card"}
    >
      {plan?.badgeText ? (
        <div className="subscription-card__badge">{plan.badgeText}</div>
      ) : null}

      <div className="subscription-card__crown">
        <img src={crown} alt="" />
      </div>

      {plan?.name ? (
        <div className="subscription-card__name">{plan.name}</div>
      ) : null}

      <div className="subscription-card__price">
        {
          <>
            {amount === null ? (
              "—"
            ) : hasSimpleInterval(plan) ? (
              <>
                {amount}
                <span className="subscription-card__interval">
                  <FormattedMessage id={intervalMessageId(plan)} />
                </span>
              </>
            ) : (
              // Falls back to Stripe's own wording rather than silently
              // rounding "every 3 months" down to "/ month".
              <>
                {amount}
                <span className="subscription-card__interval">
                  {`/ ${plan?.intervalCount} ${plan?.interval}s`}
                </span>
              </>
            )}
          </>
        }
      </div>

      {/* The slot is always rendered so a plan with a trial and one without still
          line up. Monthly carries a trial and annual does not, so making this
          conditional would misalign the two cards exactly as the description did. */}
      <div className="subscription-card__trial-slot">
        {showTrial && plan?.trialMonth > 0 ? (
          <span className="subscription-card__trial">
            <FormattedMessage id="free_trail_month" />
          </span>
        ) : null}
      </div>

      {/* Rendered even when empty so the divider, features and CTA line up
          across cards whether or not a plan has a description. */}
      <div className="subscription-card__description">
        {plan?.description || ""}
      </div>

      <div className="sub-divider"></div>

      <ul className="subscription-card__features">
        {featureKeys.map((key) => (
          <li key={key} className="subscription-card__feature">
            <img src={tick} alt="" />
            <span>
              <FormattedMessage id={key} />
            </span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onCta}
        className="sub-button fs-4 fw-bold"
        loading={ctaLoading}
        disabled={ctaDisabled}
      >
        {ctaLabel}
      </Button>
    </div>
  );
};

export default PlanCard;
